import React from "react";
import { BudgetCategory, BudgetContingency, BudgetI } from "../types";
import {
  getAboveTheLineEstimate,
  getBelowTheLineEstimate,
  getBudgetEstimateContingency,
  getBudgetItemEstimate,
  getBudgetItemsEstimate,
  getBudgetTotalEstimate,
  getCategoryFringesInBudget,
  getFringeAccountNumber,
} from "../BudgetItemUtils";
import classNames from "classnames";
import { formatNumberWithCommas } from "utils";
import { BudgetAccountHeadRow } from "./rows/BudgetAccountHeadRow";
import { BudgetEmptyRow } from "./rows/BudgetEmptyRow";
import { CURRENCIES } from "config";

interface DetailedBudgetGridPrintProp {
  data: BudgetCategory[];
  project: string;
  budgetDetail: Omit<BudgetI, "categories"> | undefined;
  budgetContingency: BudgetContingency | null;
}

interface FormatCellMoneyParams {
  value: number | null | undefined;
  emptyIfNoValue?: boolean;
  currency: string;
}

export const formatCellMoney = ({
  value,
  emptyIfNoValue = true,
  currency,
}: FormatCellMoneyParams) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "";
  }
  if (value === 0 && emptyIfNoValue) {
    return "";
  }
  const selectedCurrency = CURRENCIES.find((cur) => cur.abbreviation === currency.toUpperCase());
  return `${selectedCurrency?.symbol ?? "$"}${formatNumberWithCommas(value, emptyIfNoValue)}`;
};

export const DetailedBudgetGridPrint: React.FC<DetailedBudgetGridPrintProp> = ({
  data,
  budgetDetail,
  budgetContingency,
}) => {
  const _light_bg = "bg-[#f2f2f2]";
  const cell_data_style = "border h-[24px] px-2";

  const currency = budgetDetail?.currency ?? "USD";

  return (
    <table className="min-w-full border-border border-collapse border text-left text-[14px]">
      <tbody className="font-light text-left relative text-black">
        {data.map((rowitem, row_key) => {
          const categoryFringes = getCategoryFringesInBudget(rowitem, data);

          return (
            <React.Fragment key={row_key + rowitem.budget_category_id}>
              {rowitem.sub_categories.map((subrow, subkey) => (
                <React.Fragment key={subkey + "tk"}>
                  <BudgetAccountHeadRow />
                  <BudgetEmptyRow cellClassName={cell_data_style} />
                  <tr className="font-bold">
                    <td className={cell_data_style}>
                      <div>
                        <div>{subrow.account_number}</div>
                      </div>
                    </td>
                    <td className={classNames(cell_data_style, "w-[200px]")}>
                      <div>{subrow.name}</div>
                    </td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                  </tr>
                  <BudgetEmptyRow cellClassName={cell_data_style} />
                  {subrow.items.map((item, itemkey) => (
                    <React.Fragment key={itemkey + "itk"}>
                      <tr>
                        <td className={cell_data_style}>
                          <div>
                            <div>{item.account_number}</div>
                          </div>
                        </td>
                        <td className={classNames(cell_data_style, "w-[200px]")}>
                          <div className="">{item.name}</div>
                        </td>
                        <td className={`${cell_data_style} uppercase`}>
                          {item.children.length === 0 && <div>{item.unit}</div>}
                        </td>
                        <td className={cell_data_style}>
                          {item.children.length === 0 && <div>{item.quantity}</div>}
                        </td>
                        <td className={cell_data_style}>
                          {item.children.length === 0 && (
                            <div>
                              {item.rate
                                ? formatCellMoney({
                                    value: item.rate,
                                    currency,
                                  })
                                : ""}
                            </div>
                          )}
                        </td>
                        <td className={cell_data_style}>
                          {item.children.length === 0 && <div>{item.x_factor}</div>}
                        </td>
                        <td className={cell_data_style}>
                          {formatCellMoney({ value: getBudgetItemEstimate(item), currency })}
                        </td>
                        <td className={cell_data_style}></td>
                      </tr>
                      {item.children.map((child, childkey) => (
                        <React.Fragment key={childkey + "ictk"}>
                          <tr>
                            <td className={cell_data_style}>
                              <div>
                                <div>{child.account_number}</div>
                              </div>
                            </td>
                            <td className={classNames(cell_data_style, "w-[200px]")}>
                              <div className="text-right">{child.name}</div>
                            </td>
                            <td className={`${cell_data_style} uppercase`}>
                              <div>{child.unit}</div>
                            </td>
                            <td className={cell_data_style}>
                              <div>{child.quantity}</div>
                            </td>
                            <td className={cell_data_style}>
                              <div>{formatCellMoney({ value: child.rate, currency })}</div>
                            </td>
                            <td className={cell_data_style}>
                              <div>{child.x_factor}</div>
                            </td>
                            <td className={cell_data_style}>
                              {formatCellMoney({ value: getBudgetItemEstimate(child), currency })}
                            </td>
                            <td className={cell_data_style}></td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr className="font-bold">
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}>
                      {formatCellMoney({ value: getBudgetItemsEstimate(subrow.items), currency })}
                    </td>
                  </tr>
                  <BudgetEmptyRow cellClassName={cell_data_style} />
                </React.Fragment>
              ))}
              {categoryFringes.nested_fringes.length > 0 && (
                <>
                  <BudgetAccountHeadRow />
                  <BudgetEmptyRow cellClassName={cell_data_style} />

                  <tr key={row_key + "tk-fringes"} className="font-bold">
                    <td className={classNames(cell_data_style, " ")}>
                      {rowitem.fringe_account_number}
                    </td>
                    <td className={cell_data_style}>
                      <div>{rowitem.name} FRINGES</div>
                    </td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                  </tr>
                </>
              )}
              {categoryFringes.nested_fringes.map((fringe, fringekey) => (
                <React.Fragment key={fringekey + "fringe"}>
                  <tr>
                    <td className={cell_data_style}>
                      {rowitem.fringe_account_number &&
                        getFringeAccountNumber(rowitem.fringe_account_number, fringekey)}
                    </td>
                    <td className={cell_data_style}>
                      <div className="text-right">{fringe.name}</div>
                    </td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}>
                      <div></div>
                    </td>
                    <td className={cell_data_style}>
                      <div>{fringe.rate.toFixed(2)}%</div>
                    </td>
                    <td className={cell_data_style}>
                      {formatCellMoney({ value: fringe.estimate, currency })}
                    </td>
                    <td className={cell_data_style}></td>
                  </tr>
                </React.Fragment>
              ))}
              {categoryFringes.nested_fringes.length > 0 && (
                <>
                  <tr key={row_key + "tk-fringes"} className="font-bold">
                    <td className={classNames(cell_data_style, " ")}>
                      {rowitem.fringe_account_number}
                    </td>
                    <td className={cell_data_style}>
                      <div>{rowitem.name} FRINGES</div>
                    </td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}></td>
                    <td className={cell_data_style}>
                      {formatCellMoney({ value: categoryFringes.estimate, currency })}
                    </td>
                  </tr>
                  <BudgetEmptyRow cellClassName={cell_data_style} />
                </>
              )}
            </React.Fragment>
          );
        })}
        <tr className={`rounded-b-[16px]`}>
          <td className={classNames(cell_data_style, "text-right font-bold")} colSpan={7}>
            <div>TOTAL ABOVE-THE-LINE</div>
          </td>
          <td className={cell_data_style}>
            <div>{formatCellMoney({ value: getAboveTheLineEstimate(data), currency })}</div>
          </td>
        </tr>
        <tr className={`rounded-b-[16px]`}>
          <td className={classNames(cell_data_style, "text-right font-bold")} colSpan={7}>
            <div>TOTAL BELOW-THE-LINE</div>
          </td>
          <td className={cell_data_style}>
            <div>
              {formatCellMoney({
                value: getBelowTheLineEstimate(data),
                emptyIfNoValue: false,
                currency,
              })}
            </div>
          </td>
        </tr>
        <tr>
          <td className={classNames(cell_data_style, "text-right font-bold")} colSpan={7}>
            <div>CONTINGENCY</div>
          </td>
          <td className={cell_data_style}>
            <div>
              {formatCellMoney({
                value: getBudgetEstimateContingency(data, budgetContingency),
                emptyIfNoValue: false,
                currency,
              })}
              ({budgetDetail?.contingency_percentage ?? 0}%)
            </div>
          </td>
        </tr>
        <tr className={`rounded-b-[16px] font-medium`}>
          <td className={classNames(cell_data_style, "text-right font-bold")} colSpan={7}>
            <div>GRAND TOTAL</div>
          </td>
          <td className={cell_data_style}>
            <div>
              {formatCellMoney({
                value: getBudgetTotalEstimate(data, budgetContingency),
                emptyIfNoValue: false,
                currency,
              })}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
